import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, Users, Shield, Check, X, Eye, EyeOff, Lock, Monitor, LogOut, RefreshCw, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULES = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'demand', label: 'Demand' },
  { key: 'mrp', label: 'MRP' },
  { key: 'procurement', label: 'Milk Run Orders' },
  { key: 'shipment', label: 'Shipment' },
  { key: 'shipment_vendor', label: 'Shipment - Vendor Portal (Create & Ready)' },
  { key: 'shipment_driver', label: 'Shipment - Driver Portal (In Transit)' },
  { key: 'shipment_store', label: 'Shipment - Store Portal (Receive & GRN)' },
  { key: 'grn', label: 'GRN' },
  { key: 'production', label: 'Production' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'customer_returns', label: 'Customer Returns' },
  { key: 'rework', label: 'Rework Production' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'order_tracking', label: 'Order Tracking' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'masters', label: 'Masters' }
];

const ACTIONS = ['view', 'create', 'edit', 'delete'];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [plants, setPlants] = useState([]);
  const [userDialog, setUserDialog] = useState({ open: false, user: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, role: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null });

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role_id: '',
    vendor_id: '',
    is_active: true,
    accessible_plants: [],
    plant_id: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, userId: null });
  const [newPassword, setNewPassword] = useState('');

  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  // Active Sessions state
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [forceLogoutDialog, setForceLogoutDialog] = useState({ open: false, user: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, vendorsRes, plantsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/roles`),
        axios.get(`${API}/vendors`),
        axios.get(`${API}/plants`)
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setVendors(vendorsRes.data);
      setPlants(plantsRes.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user management data');
    }
  };

  // Fetch active sessions
  const fetchActiveSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await axios.get(`${API}/auth/active-sessions`);
      setActiveSessions(response.data.active_sessions || []);
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      toast.error('Failed to load active sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  // Force logout a user
  const handleForceLogout = async () => {
    if (!forceLogoutDialog.user) return;
    
    try {
      await axios.post(`${API}/auth/force-logout/${forceLogoutDialog.user.id}`);
      toast.success(`${forceLogoutDialog.user.username} has been logged out`);
      setForceLogoutDialog({ open: false, user: null });
      fetchActiveSessions(); // Refresh the list
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to logout user';
      toast.error(errorMsg);
    }
  };

  // Format date for display
  const formatSessionDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateUser = () => {
    setNewUser({ username: '', email: '', password: '', role_id: '', vendor_id: '', is_active: true, accessible_plants: [], plant_id: '' });
    setShowPassword(false);
    setUserDialog({ open: true, user: null });
  };

  const handleEditUser = (user) => {
    const roleId = user.role_id || '';
    
    setNewUser({
      username: user.username,
      email: user.email,
      password: '',
      role_id: roleId,
      vendor_id: user.vendor_id || '',
      is_active: user.is_active,
      accessible_plants: user.accessible_plants || [],
      plant_id: user.plant_id || ''
    });
    setShowPassword(false);
    setUserDialog({ open: true, user });
  };
  
  const handleResetPassword = (userId) => {
    setResetPasswordDialog({ open: true, userId });
    setNewPassword('');
  };
  
  const handleConfirmResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/users/${resetPasswordDialog.userId}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordDialog({ open: false, userId: null });
      setNewPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMsg);
    }
  };

  const handleSaveUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.role_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Check if selected role requires a vendor assignment
    const selectedRole = roles.find(r => r.id === newUser.role_id);
    const isVendorRoleSelected = selectedRole?.name?.toLowerCase().includes('vendor');
    
    if (isVendorRoleSelected && !newUser.vendor_id) {
      toast.error('Please select a vendor for this vendor role');
      return;
    }

    // Validate plant assignment
    if (!newUser.accessible_plants || newUser.accessible_plants.length === 0) {
      toast.error('Please assign at least one plant to the user');
      return;
    }
    
    // Validate password for new users
    if (!userDialog.user && (!newUser.password || newUser.password.length < 6)) {
      toast.error('Password is required and must be at least 6 characters');
      return;
    }

    try {
      if (userDialog.user) {
        // Update (without password)
        const updateData = {
          username: newUser.username,
          email: newUser.email,
          role_id: newUser.role_id,
          vendor_id: newUser.vendor_id || null,
          is_active: newUser.is_active,
          accessible_plants: newUser.accessible_plants,
          plant_id: newUser.plant_id || newUser.accessible_plants[0] || null
        };
        await axios.put(`${API}/users/${userDialog.user.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        // Create (with password)
        const userData = {
          ...newUser,
          vendor_id: newUser.vendor_id || null,
          plant_id: newUser.plant_id || newUser.accessible_plants[0] || null
        };
        await axios.post(`${API}/users`, userData);
        toast.success('User created successfully');
      }
      
      setUserDialog({ open: false, user: null });
      setTimeout(async () => {
        await fetchData();
      }, 300);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save user';
      toast.error(errorMsg);
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/toggle-active`);
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = (userId) => {
    setDeleteDialog({ open: true, type: 'user', id: userId });
  };

  const confirmDelete = async () => {
    try {
      if (deleteDialog.type === 'user') {
        await axios.delete(`${API}/users/${deleteDialog.id}`);
        toast.success('User deleted successfully');
      } else if (deleteDialog.type === 'role') {
        await axios.delete(`${API}/roles/${deleteDialog.id}`);
        toast.success('Role deleted successfully');
      }
      setDeleteDialog({ open: false, type: '', id: null });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete';
      toast.error(errorMsg);
    }
  };

  // Role Management
  const handleCreateRole = () => {
    const defaultPermissions = {};
    MODULES.forEach(module => {
      defaultPermissions[module.key] = {
        view: false,
        create: false,
        edit: false,
        delete: false
      };
    });
    
    setNewRole({ name: '', description: '', permissions: defaultPermissions });
    setRoleDialog({ open: true, role: null });
  };

  const handleEditRole = (role) => {
    // Ensure all module permissions exist with default values
    const defaultPermissions = {};
    MODULES.forEach(module => {
      defaultPermissions[module.key] = {
        view: false,
        create: false,
        edit: false,
        delete: false,
        ...(role.permissions?.[module.key] || {})
      };
    });
    
    setNewRole({
      name: role.name,
      description: role.description || '',
      permissions: defaultPermissions
    });
    setRoleDialog({ open: true, role });
  };

  const handleSaveRole = async () => {
    if (!newRole.name) {
      toast.error('Please enter role name');
      return;
    }

    try {
      if (roleDialog.role) {
        // Update
        await axios.put(`${API}/roles/${roleDialog.role.id}`, newRole);
        toast.success('Role updated successfully');
      } else {
        // Create
        await axios.post(`${API}/roles`, newRole);
        toast.success('Role created successfully');
      }
      setRoleDialog({ open: false, role: null });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save role';
      toast.error(errorMsg);
    }
  };

  const handleDeleteRole = (roleId) => {
    setDeleteDialog({ open: true, type: 'role', id: roleId });
  };

  const updatePermission = (moduleKey, action, value) => {
    setNewRole(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          ...prev.permissions[moduleKey],
          [action]: value
        }
      }
    }));
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  const getVendorName = (vendorId) => {
    if (!vendorId) return '-';
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor ? vendor.name : vendorId;
  };

  const isVendorRole = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name?.toLowerCase().includes('vendor') || false;
  };

  const getPlantDisplay = (plantIds) => {
    if (!plantIds || plantIds.length === 0) return [];
    return plantIds.map(pid => {
      const plant = plants.find(p => p.id === pid);
      return plant ? { id: pid, code: plant.code, name: plant.name } : { id: pid, code: pid, name: pid };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-slate-600">Manage users, roles, and permissions</p>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles">
            <Shield className="w-4 h-4 mr-2" />
            Roles & Permissions
          </TabsTrigger>
          <TabsTrigger value="sessions" onClick={fetchActiveSessions}>
            <Monitor className="w-4 h-4 mr-2" />
            Active Sessions
          </TabsTrigger>
          <TabsTrigger value="system">
            <Lock className="w-4 h-4 mr-2" />
            System Admin
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Users</CardTitle>
                  <CardDescription>Manage user accounts and assign roles</CardDescription>
                </div>
                <Button onClick={handleCreateUser} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p>No users yet. Create your first user above.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Username</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Plants</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Vendor</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm font-medium">{user.username}</td>
                          <td className="px-4 py-3 text-sm">{user.email}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{getRoleName(user.role_id)}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {getPlantDisplay(user.accessible_plants).map(p => (
                                <Badge key={p.id} variant="outline" className="bg-teal-50 text-teal-700 text-xs whitespace-nowrap">
                                  {p.code}
                                </Badge>
                              ))}
                              {(!user.accessible_plants || user.accessible_plants.length === 0) && (
                                <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">None</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600">{getVendorName(user.vendor_id)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditUser(user)}
                                title="Edit User"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResetPassword(user.id)}
                                className="text-blue-600"
                                title="Reset Password"
                              >
                                <Lock className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleUserActive(user.id)}
                                title={user.is_active ? "Deactivate" : "Activate"}
                              >
                                {user.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteUser(user.id)}
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Roles & Permissions</CardTitle>
                  <CardDescription>Manage roles and their module-level permissions</CardDescription>
                </div>
                <Button onClick={handleCreateRole} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Custom Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => (
                  <Card key={role.id} className={role.is_default ? 'border-blue-200 bg-blue-50' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-lg">{role.name}</CardTitle>
                            {role.is_default && (
                              <Badge className="bg-blue-600">Default</Badge>
                            )}
                          </div>
                          <CardDescription>{role.description}</CardDescription>
                        </div>
                        {!role.is_default && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditRole(role)}
                              data-testid={`edit-role-${role.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteRole(role.id)}
                              data-testid={`delete-role-${role.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                        {role.is_default && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditRole(role)}
                              data-testid={`edit-default-role-${role.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xs text-slate-600 space-y-1">
                        <strong>Permissions:</strong>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                          {MODULES.map(module => {
                            const perms = role.permissions?.[module.key] || {};
                            const actions = [];
                            if (perms.view) actions.push('View');
                            if (perms.create) actions.push('Create');
                            if (perms.edit) actions.push('Edit');
                            if (perms.delete) actions.push('Delete');
                            
                            if (actions.length === 0) return null;
                            
                            return (
                              <div key={module.key} className="text-xs bg-white p-2 rounded border">
                                <strong>{module.label}:</strong> {actions.join(', ')}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Sessions Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    Active Sessions
                  </CardTitle>
                  <CardDescription>
                    View and manage currently logged in users
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchActiveSessions}
                  disabled={sessionsLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${sessionsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {sessionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-500">Loading sessions...</span>
                </div>
              ) : activeSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Monitor className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No active sessions found</p>
                  <p className="text-sm">Click Refresh to load current sessions</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>IP Address</TableHead>
                        <TableHead>Browser</TableHead>
                        <TableHead>Login Time</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeSessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{session.username}</div>
                              <div className="text-sm text-slate-500">{session.email}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{session.role_name}</Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                              {session.session_ip || 'Unknown'}
                            </code>
                          </TableCell>
                          <TableCell>
                            {session.session_browser || 'Unknown'}
                          </TableCell>
                          <TableCell>
                            {formatSessionDate(session.session_created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setForceLogoutDialog({ open: true, user: session })}
                            >
                              <LogOut className="w-4 h-4 mr-1" />
                              Force Logout
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>Note:</strong> Force logout will immediately end the user's session. 
                They will be redirected to the login page on their next action.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Admin Tab */}
        <TabsContent value="system">
          <Card className="border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="text-red-900 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                System Administration
              </CardTitle>
              <CardDescription className="text-red-700">
                Dangerous operations - Use with caution!
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Reset Transactional Data */}
                <div className="border-2 border-orange-300 rounded-lg p-6 bg-orange-50">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-orange-900 mb-2">
                        Reset Transactional Data
                      </h3>
                      <p className="text-sm text-orange-700 mb-4">
                        Clear all transactional data to start fresh testing. This will delete:
                      </p>
                      <ul className="text-sm text-orange-800 space-y-1 mb-4 ml-4">
                        <li>• All Milk Run Orders (MROs)</li>
                        <li>• All Shipments & GRNs</li>
                        <li>• All Production Orders</li>
                        <li>• All Inventory Transactions</li>
                        <li>• All Customer Schedules (Daily & Monthly)</li>
                        <li>• All Demand Data</li>
                        <li>• All MRP Requirements</li>
                      </ul>
                      <div className="bg-green-50 border border-green-300 rounded p-3 mb-4">
                        <p className="text-sm text-green-800 font-medium">
                          ✅ Master Data Will Be Preserved:
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Users, Vendors, Customers, Items, BOM, Sourcing, Warehouses, etc.
                        </p>
                      </div>
                      <Button
                        onClick={async () => {
                          if (window.confirm('⚠️ WARNING: This will delete ALL transactional data!\n\nMaster data will be preserved.\n\nAre you absolutely sure you want to continue?')) {
                            try {
                              toast.loading('Resetting transactional data...');
                              const response = await axios.post(`${API}/system/reset-transactional-data`);
                              toast.dismiss();
                              toast.success('Transactional data reset successfully!', {
                                description: `${response.data.inventory_reset} inventory items reset to 0`,
                                duration: 5000
                              });
                            } catch (error) {
                              toast.dismiss();
                              toast.error('Failed to reset data', {
                                description: error.response?.data?.detail || error.message
                              });
                            }
                          }
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Reset All Transactional Data
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{userDialog.user ? 'Edit User' : 'Create User'}</DialogTitle>
            <DialogDescription>
              {userDialog.user ? 'Update user details' : 'Add a new user to the system'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Username *</label>
              <Input
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="johndoe"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Email *</label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            {!userDialog.user && (
              <div>
                <label className="text-sm font-medium mb-2 block">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Minimum 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Password must be at least 6 characters</p>
              </div>
            )}
            
            {userDialog.user && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  To change this user's password, use the "Reset Password" button from the user list.
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Role *</label>
              <select
                key={`role-select-${userDialog.user?.id || 'new'}-${newUser.role_id}`}
                className="w-full border border-slate-300 rounded-md p-2"
                value={newUser.role_id}
                onChange={(e) => {
                  console.log('Role changed to:', e.target.value);
                  setNewUser({ ...newUser, role_id: e.target.value });
                }}
              >
                <option value="">Select role...</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>

            {isVendorRole(newUser.role_id) && (
              <div>
                <label className="text-sm font-medium mb-2 block">Vendor *</label>
                <select
                  key={`vendor-select-${userDialog.user?.id || 'new'}-${newUser.vendor_id}`}
                  className="w-full border border-slate-300 rounded-md p-2"
                  value={newUser.vendor_id}
                  onChange={(e) => {
                    setNewUser({ ...newUser, vendor_id: e.target.value });
                  }}
                >
                  <option value="">Select vendor...</option>
                  {vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  This user will only see data for the selected vendor
                </p>
              </div>
            )}

            {/* Plant Assignment */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
                <Factory className="w-4 h-4 text-teal-600" />
                Accessible Plants *
              </label>
              <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto bg-white">
                {plants.map(plant => (
                  <label key={plant.id} className="flex items-center gap-2.5 cursor-pointer hover:bg-slate-50 p-1.5 rounded" data-testid={`user-plant-checkbox-${plant.id}`}>
                    <Checkbox
                      checked={newUser.accessible_plants.includes(plant.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewUser(prev => ({ ...prev, accessible_plants: [...prev.accessible_plants, plant.id] }));
                        } else {
                          setNewUser(prev => ({ ...prev, accessible_plants: prev.accessible_plants.filter(id => id !== plant.id) }));
                        }
                      }}
                    />
                    <span className="text-sm">{plant.code} - {plant.name}</span>
                  </label>
                ))}
              </div>
              {newUser.accessible_plants.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Select at least one plant</p>
              )}
              {newUser.accessible_plants.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  {newUser.accessible_plants.length} plant(s) selected. Default plant: {(() => {
                    const defId = newUser.plant_id || newUser.accessible_plants[0];
                    const p = plants.find(pl => pl.id === defId);
                    return p ? `${p.code} - ${p.name}` : defId;
                  })()}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={newUser.is_active}
                onChange={(e) => setNewUser({ ...newUser, is_active: e.target.checked })}
              />
              <label htmlFor="is_active" className="text-sm">Active</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser}>
              {userDialog.user ? 'Update' : 'Create'} User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Dialog */}
      <Dialog open={roleDialog.open} onOpenChange={(open) => setRoleDialog({ open, role: null })}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{roleDialog.role ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
            <DialogDescription>
              Define permissions for each module
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Role Name *</label>
              <Input
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                placeholder="Custom Role"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Input
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                placeholder="Role description"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Permissions</label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Module</th>
                      <th className="px-4 py-2 text-center font-semibold">View</th>
                      <th className="px-4 py-2 text-center font-semibold">Create</th>
                      <th className="px-4 py-2 text-center font-semibold">Edit</th>
                      <th className="px-4 py-2 text-center font-semibold">Delete</th>
                      <th className="px-4 py-2 text-center font-semibold">Set Zero Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {MODULES.map(module => (
                      <tr key={module.key} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">{module.label}</td>
                        {ACTIONS.map(action => (
                          <td key={action} className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={newRole.permissions[module.key]?.[action] || false}
                              onChange={(e) => updatePermission(module.key, action, e.target.checked)}
                            />
                          </td>
                        ))}
                        <td className="px-4 py-2 text-center">
                          {module.key === 'production' ? (
                            <input
                              type="checkbox"
                              checked={newRole.permissions[module.key]?.set_zero_qty || false}
                              onChange={(e) => updatePermission(module.key, 'set_zero_qty', e.target.checked)}
                              data-testid="perm-production-set-zero-qty"
                            />
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog({ open: false, role: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveRole}>
              {roleDialog.role ? 'Update' : 'Create'} Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onOpenChange={(open) => setResetPasswordDialog({ open, userId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset User Password</DialogTitle>
            <DialogDescription>
              Enter a new password for this user
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">New Password *</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Password must be at least 6 characters</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialog({ open: false, userId: null })}>
              Cancel
            </Button>
            <Button onClick={handleConfirmResetPassword} className="bg-blue-600 hover:bg-blue-700">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, type: '', id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDialog.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the {deleteDialog.type}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Logout Confirmation Dialog */}
      <AlertDialog open={forceLogoutDialog.open} onOpenChange={(open) => setForceLogoutDialog({ open, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force Logout User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to force logout <strong>{forceLogoutDialog.user?.username}</strong>?
              <br /><br />
              Their current session will be terminated immediately and they will be redirected to the login page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceLogout}
              className="bg-red-600 hover:bg-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Force Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
